import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

function SeriesEditDialog({ open, onClose, onConfirm, isRecurring, actionType = 'edit', disabled = false }) {
  const { t } = useTranslation();
  const [editType, setEditType] = React.useState('occurrence');
  const [submitting, setSubmitting] = React.useState(false);
  const autoConfirmAttemptedRef = React.useRef(false);

  const handleConfirm = async () => {
    if (disabled) return;
    setSubmitting(true);
    try {
      const saved = await onConfirm(editType);
      if (saved !== false) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setEditType('occurrence');
    onClose();
  };

  // For non-recurring events, immediately confirm with 'occurrence'. Attempt
  // at most once per opening: command admission changes `disabled` and the
  // onConfirm callback identity while the request is in flight.
  React.useEffect(() => {
    if (!open) {
      autoConfirmAttemptedRef.current = false;
      return;
    }
    if (isRecurring || autoConfirmAttemptedRef.current) return;

    autoConfirmAttemptedRef.current = true;
    if (disabled) {
      onClose();
      return;
    }
    Promise.resolve(onConfirm('occurrence'))
      .catch(() => false)
      .finally(onClose);
  }, [disabled, open, isRecurring, onConfirm, onClose]);

  if (!isRecurring) {
    return null;
  }

  const isDelete = actionType === 'delete';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isDelete
          ? t('calendar.deleteRecurringEvent', 'Delete Recurring Event')
          : t('calendar.editRecurringEvent', 'Edit Recurring Event')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {isDelete
            ? t('calendar.deleteRecurringEventDescription', 'This is a recurring event. What would you like to delete?')
            : t('calendar.editRecurringEventDescription', 'This is a recurring event. What would you like to change?')}
        </DialogContentText>
        <FormControl component="fieldset">
          <RadioGroup value={editType} onChange={e => setEditType(e.target.value)}>
            <Stack spacing={1}>
              <FormControlLabel
                value="occurrence"
                control={<Radio />}
                label={
                  <Stack>
                    <Typography variant="body1">
                      {isDelete
                        ? t('calendar.deleteThisOccurrence', 'Delete this occurrence')
                        : t('calendar.editThisOccurrence', 'Edit this occurrence')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isDelete
                        ? t('calendar.deleteThisOccurrenceDesc', 'Only this instance will be deleted')
                        : t('calendar.editThisOccurrenceDesc', 'Only this instance will be changed')}
                    </Typography>
                  </Stack>
                }
              />
              <FormControlLabel
                value="series"
                control={<Radio />}
                label={
                  <Stack>
                    <Typography variant="body1">
                      {isDelete
                        ? t('calendar.deleteEntireSeries', 'Delete entire series')
                        : t('calendar.editEntireSeries', 'Edit entire series')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isDelete
                        ? t('calendar.deleteEntireSeriesDesc', 'All instances of this recurring event will be deleted')
                        : t('calendar.editEntireSeriesDesc', 'All instances of this recurring event will be changed')}
                    </Typography>
                  </Stack>
                }
              />
            </Stack>
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('actions.cancel', 'Cancel')}</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isDelete ? 'error' : 'primary'}
          disabled={disabled || submitting}
        >
          {isDelete ? t('actions.delete', 'Delete') : t('actions.edit', 'Edit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SeriesEditDialog;
